<template>
    <div class="date-timer-process-modal">
        <TimerModal
            data-qa="date-timer-modal"
            :title="$t('funnel.edit.timer.date.title')"
            :timer-tip-link="$t('funnel.edit.timer.field.proTipUrl')"
            :timer-tip-text="$t('funnel.edit.timer.field.proTip')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #timerSummary>
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="summaryText" />
                <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #timerParameters>
                <div class="progress-slider">
                    <div v-if="hasErrors" class="errorMessage">
                        <DsInlineAlert leading-icon type="critical">
                            {{ validationErrorMessages }}
                        </DsInlineAlert>
                    </div>
                    <div class="date-timer-formLabel top-border">
                        {{ $t('funnel.edit.timer.run') }}
                    </div>
                    <div class="top-border-triple-wrapper">
                        <div class="formSpacer">
                            <DsRadioGroup
                                v-model="dateType"
                                class="timer-horizontal-radio"
                                data-qa="date-timer-date-of-day-type"
                                :options="dateTypes"
                            />
                        </div>
                    </div>
                    <div>
                        <DsDatePicker
                            v-if="isDateTypeOn"
                            v-model="startDate"
                            :label="$t('funnel.delay.timer.date')"
                            data-qa="date-timer-date-start-date"
                            class="medium-date-picker"
                        />
                        <DsDatePicker
                            v-if="isDateTypeBetween"
                            v-model="rangeDate"
                            :label="$t('funnel.delay.timer.date')"
                            range
                            data-qa="date-timer-date-end-date"
                            class="timer-horizontal-date-unique"
                        />
                    </div>
                    <div class="formSpacer">
                        <DsRadioGroup
                            v-model="timeOfDayType"
                            class="timer-horizontal-radio"
                            data-qa="date-timer-time-of-day-type-radio"
                            :options="timeOfDayTypes"
                        />
                    </div>
                    <div class="timer-horizontal-date">
                        <DsDatePicker
                            v-if="isTimeOfDateAt || isTimeOfDateBetween"
                            v-model="startTime"
                            :label="isTimeOfDateAt ? $t('funnel.delay.timer.time') : $t('funnel.delay.timer.startTime')"
                            :force-invalid="invalidStartTime"
                            time-only
                            :allow-input="false"
                            :minute-increment="minuteIncrement"
                            data-qa="date-timer-time-of-day"
                            class="small-date-picker"
                        >
                            <template #error>
                                <span v-if="isMounted && invalidStartTime" data-qa="invalid-start-time">
                                    {{ $t('funnel.edit.timer.invalid') }}
                                </span>
                            </template>
                        </DsDatePicker>
                        <DsDatePicker
                            v-if="isTimeOfDateBetween"
                            v-model="stopTime"
                            time-only
                            :force-invalid="invalidStopTime || isStopTimeBeforeThanStartTime"
                            :allow-input="false"
                            :minute-increment="minuteIncrement"
                            :label="$t('funnel.delay.timer.endTime')"
                            data-qa="date-timer-time-of-day-stop"
                            class="small-date-picker"
                        >
                            <template #error>
                                <span v-if="isMounted && invalidStopTime" data-qa="invalid-stop-time">
                                    {{ $t('funnel.edit.timer.invalid') }}
                                </span>
                                <span v-if="isMounted && isStopTimeBeforeThanStartTime" data-qa="invalid-stop-time">
                                    {{ $t('funnel.edit.timer.invalid.interval') }}
                                </span>
                            </template>
                        </DsDatePicker>
                    </div>
                    <div class="formSpacer">
                        <DsCheckbox
                            v-model="useContactTimeZone"
                            data-qa="date-timer-use-contact-tz"
                        >
                            {{ $t('funnel.edit.timer.useContactTimeZoneWhenAvailable') }}
                        </DsCheckbox>
                    </div>
                </div>
            </template>
        </TimerModal>
    </div>
</template>

<script>
import TimerModal from '../timerModal/TimerModal.vue';

import {
    TIMER_DATE,
    TIMERS,
    TIMERS_TIME_INCREMENT,
} from '../../../../utils/editor.constants';
import {
    createDate,
    createTime,
    isTimeBefore,
    isValidMinute,
    TIME_DAY_TYPES,
    cleanUpTimeFields,
    getDay,
    getMonth,
    getYear,
} from '../timer.utils';
import { findIncomingCells, findOutgoingCells } from '../../../../utils/cell/automation-edge-utils';

export const DATE_TYPES = {
    ON: 'On',
    BETWEEN: 'Between',
};

const isNumeric = (n) => {
    return !Number.isNaN(parseFloat(n)) && Number.isFinite(n);
};

export default {
    components: {
        TimerModal,
    },

    props: {
        cells: {
            type: Array,
            default: () => ([]),
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    dateType: '',
                    startDay: null,
                    startMonth: null,
                    startYear: null,
                    stopDay: null,
                    stopMonth: null,
                    stopYear: null,
                    timeOfDayType: '',
                    startHour: '',
                    startMinute: '',
                    startAmPm: '',
                    stopHour: '',
                    stopMinute: '',
                    stopAmPm: '',
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            isMounted: false,
            minuteIncrement: Number(TIMERS_TIME_INCREMENT),
            currentDate: new Date(),
            dateType: this.cell?.value?.dateType ?? 'On',
            dateTypes: [{ value: DATE_TYPES.ON, label: this.$t('funnel.edit.timer.date.on') }, { value: DATE_TYPES.BETWEEN, label: this.$t('funnel.edit.timer.date.between') }],
            startDate: new Date(),
            rangeDate: [new Date(), new Date()],
            startTime: '0800',
            stopTime: '1700',
            timeOfDayType: this.cell?.value?.timeOfDayType ?? TIME_DAY_TYPES.AT,
            useContactTimeZone: this.cell?.value?.useContactTimeZone === '1',
            timeOfDayTypes: [{ value: TIME_DAY_TYPES.AT, label: this.$t('funnel.edit.timer.timeOfDay.at') }, { value: TIME_DAY_TYPES.BETWEEN, label: this.$t('funnel.edit.timer.date.between') }, { value: TIME_DAY_TYPES.ANY, label: this.$t('delay.timer.at.any.time.upper.case') }],
            cellType: TIMER_DATE,
            validationErrorMessages: null,
            startHour: this.cell?.value?.startHour ?? '8',
            startMinute: this.cell?.value?.startMinute ?? '0',
            startAmPm: this.cell?.value?.startAmPm ?? 'AM',
            stopHour: this.cell?.value?.stopHour ?? '5',
            stopMinute: this.cell?.value?.stopMinute ?? '0',
            stopAmPm: this.cell?.value?.stopAmPm ?? 'PM',
            startMonth: getMonth(this.cell?.value?.startMonth),
            startYear: getYear(this.cell?.value?.startYear),
            startDay: getDay(this.cell?.value?.startDay),
            stopMonth: getMonth(this.cell?.value?.stopMonth),
            stopYear: getYear(this.cell?.value?.stopYear),
            stopDay: getDay(this.cell?.value?.stopDay),
        };
    },

    computed: {
        isStopTimeBeforeThanStartTime() {
            return Number(this.stopTime) <= Number(this.startTime);
        },

        invalidStartTime() {
            return !this.startTime || !this.startMinute || !isValidMinute(this.startMinute);
        },

        invalidStopTime() {
            return !this.stopMinute || !isValidMinute(this.stopMinute);
        },
        summaryAsCellLabel() {
            let summaryTextTimeZone = '';

            if (this.useContactTimeZone) {
                summaryTextTimeZone = ` ${this.$t('funnel.edit.timer.field.contactTZ')}`;
            } else {
                summaryTextTimeZone = ` ${this.appInfo.appTimeZoneId}`;
            }

            return this.summaryText.replaceAll('<b>', '').replaceAll('</b>', '').replaceAll(summaryTextTimeZone, '');
        },
        hasErrors() {
            return this.validationErrorMessages !== null;
        },

        isDateTypeOn() {
            return this.dateType === DATE_TYPES.ON;
        },

        isDateTypeBetween() {
            return this.dateType === DATE_TYPES.BETWEEN;
        },

        isTimeOfDateAt() {
            return this.timeOfDayType === TIME_DAY_TYPES.AT;
        },

        isTimeOfDateBetween() {
            return this.timeOfDayType === TIME_DAY_TYPES.BETWEEN;
        },

        summaryText() {
            this.handleTimeConversionBackwards();

            let summaryLabel = '';
            let summaryTextDate = '';
            let summaryTextTimeType = '';
            let summaryTextTime = '';
            let summaryTextTimeZone = '';

            const options = { year: 'numeric', month: 'short', day: 'numeric' };

            summaryLabel = this.$t('funnel.edit.timer.run');

            if (this.dateType) {
                summaryLabel = `${summaryLabel} ${this.dateType.toLowerCase()}`;

                if (this.isDateTypeBetween) {
                    if (this.rangeDate[0]) {
                        summaryTextDate = `${this.rangeDate[0].toLocaleDateString('en-US', options)}`;
                    }

                    if (this.rangeDate[1]) {
                        summaryTextDate = `${summaryTextDate} - ${this.rangeDate[1].toLocaleDateString('en-US', options)}`;
                    }
                } else if (this.startDate) {
                    summaryTextDate = `${this.startDate.toLocaleDateString('en-US', options)}`;
                }

                if (this.timeOfDayType) {
                    if (this.timeOfDayType === TIME_DAY_TYPES.BETWEEN) {
                        if (this.startTime) {
                            summaryTextTime = `${this.startHour}:${this.startMinute} ${this.startAmPm}`;
                        }

                        if (this.stopTime) {
                            summaryTextTime = `${summaryTextTime} ${this.$t('funnel.delay.timer.and')} ${this.stopHour}:${this.stopMinute} ${this.stopAmPm}`;
                        }

                        summaryTextTimeType = this.timeOfDayType.toLowerCase();
                    } else if (this.timeOfDayType === TIME_DAY_TYPES.AT) {
                        summaryTextTime = `${this.startHour}:${this.startMinute} ${this.startAmPm}`;
                        summaryTextTimeType = this.timeOfDayType.toLowerCase();
                    } else if (this.timeOfDayType === TIME_DAY_TYPES.ANY) {
                        summaryTextTimeType = this.$t('delay.timer.at.any.time');
                    }
                }
            }

            if (this.useContactTimeZone) {
                summaryTextTimeZone = ` ${this.$t('funnel.edit.timer.field.contactTZ')}`;
            } else {
                summaryTextTimeZone = ` ${this.appInfo.appTimeZoneId}`;
            }

            let returnValue = summaryLabel;

            returnValue += ` <b>${summaryTextDate}</b>`;
            returnValue += ` ${summaryTextTimeType}`;
            returnValue += ` <b>${summaryTextTime}</b>`;
            returnValue += ` <b>${summaryTextTimeZone}</b>`;

            return returnValue;
        },
    },

    mounted() {
        this.handleTimeConversion();
        this.isMounted = true;
    },

    methods: {

        closeModal() {
            this.$emit('close-modal');
        },

        getNewDate(year, month, day) {
            return new Date(year, month, day);
        },

        handleTimeConversion() {
            this.rangeDate = [];
            this.startDate = this.getNewDate(
                getYear(this.cell?.value?.startYear),
                getMonth(this.cell?.value?.startMonth),
                getDay(this.cell?.value?.startDay),
            );

            if (this.dateType === DATE_TYPES.ON) {
                this.rangeDate.push(this.startDate);
            } else {
                this.rangeDate.push(this.startDate);
                this.rangeDate.push(this.getNewDate(
                    getYear(this.cell?.value?.stopYear),
                    getMonth(this.cell?.value?.stopMonth),
                    getDay(this.cell?.value?.stopDay),
                ));
            }

            this.startHour = this.cell?.value?.startHour ?? '8';
            this.startMinute = this.cell?.value?.startMinute ?? '0';
            this.startAmPm = this.cell?.value?.startAmPm ?? 'AM';
            this.stopHour = this.cell?.value?.stopHour ?? '5';
            this.stopMinute = this.cell?.value?.stopMinute ?? '0';
            this.stopAmPm = this.cell?.value?.stopAmPm ?? 'PM';

            this.startTime = this.getTimeFromValues(this.startHour, this.startMinute, this.startAmPm);
            this.stopTime = this.getTimeFromValues(this.stopHour, this.stopMinute, this.stopAmPm);
        },

        getTimeFromValues(hour, minute, amPm) {
            let sHours = 8;
            let sMinutes = 0;

            if (hour !== null && minute !== null && amPm !== null) {
                let hours = parseInt(hour, 10);
                const minutes = parseInt(minute, 10);

                hours = (amPm === 'PM' && hours < 12 ? hours + 12 : hours);
                hours = (amPm === 'AM' && hours === 12 ? hours - 12 : hours);

                sHours = hours.toString();
                sMinutes = minutes.toString();

                sHours = (hours < 10 ? `0${sHours}` : sHours);
                sMinutes = (minutes < 10 ? `0${sMinutes}` : sMinutes);
            }

            return `${sHours}${sMinutes}`;
        },

        handleTimeConversionBackwards() {
            if (this.startTime) {
                const hourString = this.startTime.substring(0, 2);
                const minute = this.startTime.substring(2, 4);
                const hour = +hourString % 24;

                this.startHour = (hour % 12 || 12).toString();
                this.startMinute = minute.toString();
                this.startAmPm = (hour < 12 ? 'AM' : 'PM');
            }

            if (this.stopTime && this.timeOfDayType === TIME_DAY_TYPES.BETWEEN) {
                const hourString = this.stopTime.substring(0, 2);
                const minute = this.stopTime.substring(2, 4);
                const hour = +hourString % 24;

                this.stopHour = (hour % 12 || 12).toString();
                this.stopMinute = minute.toString();
                this.stopAmPm = (hour < 12 ? 'AM' : 'PM');
            } else {
                this.stopHour = null;
                this.stopMinute = null;
                this.stopAmPm = null;
            }

            if (this.dateType === DATE_TYPES.BETWEEN && this.rangeDate && this.rangeDate.length === 2) {
                this.startYear = this.rangeDate[0].getFullYear().valueOf();
                this.startMonth = this.rangeDate[0].getMonth().valueOf();
                this.startDay = this.rangeDate[0].getDate().valueOf();
                this.stopYear = this.rangeDate[1].getFullYear().valueOf();
                this.stopMonth = this.rangeDate[1].getMonth().valueOf();
                this.stopDay = this.rangeDate[1].getDate().valueOf();
            } else if (this.dateType === DATE_TYPES.ON && this.startDate) {
                this.startYear = this.startDate.getFullYear().valueOf();
                this.startMonth = this.startDate.getMonth().valueOf();
                this.startDay = this.startDate.getDate().valueOf();
                this.stopYear = null;
                this.stopMonth = null;
                this.stopDay = null;
            }
        },

        updateConfig() {
            this.handleTimeConversionBackwards();

            if (this.validateConfig()) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.dateType = this.dateType;
                cell.value.startDay = this.startDay;
                cell.value.startMonth = this.startMonth + 1;
                cell.value.startYear = this.startYear;
                cell.value.stopDay = this.stopDay;
                cell.value.stopMonth = this.stopMonth !== null && this.stopMonth !== undefined ? this.stopMonth + 1 : null;
                cell.value.stopYear = this.stopYear;
                cell.value.timeOfDayType = this.timeOfDayType;
                cell.value.startHour = this.startHour;
                cell.value.startMinute = this.startMinute;
                cell.value.startAmPm = this.startAmPm;
                cell.value.stopHour = this.stopHour;
                cell.value.stopMinute = this.stopMinute;
                cell.value.stopAmPm = this.stopAmPm;
                cell.value.useContactTimeZone = this.useContactTimeZone ? '1' : '0';
                cell.value.name = this.summaryAsCellLabel.replace(/(.{1,25})(\s|$)/g, '$1\n');
                cleanUpTimeFields(cell);

                this.$emit('update-cell-value', { cell });
            }
        },

        validateConfig() {
            let valid = true;
            let startDateLocal = this.dateType === DATE_TYPES.BETWEEN ? this.rangeDate[0] : this.startDate;
            let stopDateLocal = this.dateType === DATE_TYPES.BETWEEN ? this.rangeDate[1] : null;

            const startTimeLocal = createTime(this.startHour, this.startMinute, this.startAmPm);
            const stopTimeLocal = createTime(this.stopHour, this.stopMinute, this.stopAmPm);

            let prevTimer;
            let nextTimer;

            const incomingCells = findIncomingCells(this.cell.id, this.cells);
            const outgoingCells = findOutgoingCells(this.cell.id, this.cells);

            prevTimer = incomingCells.filter((cell) => Object.values(TIMERS).includes(cell.meta.style));
            prevTimer = prevTimer.length > 0 ? prevTimer[0] : null;
            nextTimer = outgoingCells.filter((cell) => Object.values(TIMERS).includes(cell.meta.style));
            nextTimer = nextTimer.length > 0 ? nextTimer[0] : null;

            if (startDateLocal) {
                startDateLocal = createDate(startDateLocal.getDate().valueOf(), startDateLocal.getMonth().valueOf() + 1, startDateLocal.getFullYear().valueOf());
            }

            if (stopDateLocal) {
                stopDateLocal = createDate(stopDateLocal.getDate().valueOf(), stopDateLocal.getMonth().valueOf() + 1, stopDateLocal.getFullYear().valueOf());
            }

            if (!startDateLocal || !this.validateDate(startDateLocal)) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.date.invalidStartDate');
            } else if (this.dateType === DATE_TYPES.BETWEEN && (!startDateLocal || !stopDateLocal || this.isDateEqual(startDateLocal, stopDateLocal) || this.isDateBefore(stopDateLocal, startDateLocal))) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.timerDate.stopDate');
            } else if (this.timeOfDayType === DATE_TYPES.BETWEEN && (!startTimeLocal || !stopTimeLocal || !isTimeBefore(startTimeLocal, stopTimeLocal))) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.timeOfDay.between');
            } else if (prevTimer != null && (!startTimeLocal || !startDateLocal || this.isBeforeOrEqualToPrevious(startDateLocal, startTimeLocal, prevTimer))) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.date.after');
            } else if (nextTimer != null && this.isAfterOrEqualToNext(this.dateType, startDateLocal, stopDateLocal, this.timeOfDayType, startTimeLocal, stopTimeLocal, nextTimer)) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.date.before');
            } else if (this.dateType === DATE_TYPES.BETWEEN && (!stopDateLocal || !this.validateDate(stopDateLocal))) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.edit.timer.validation.date.invalidStopDate');
            } else if (this.timeOfDayType === TIME_DAY_TYPES.AT && !isValidMinute(this.startMinute)
                    || (this.timeOfDayType === TIME_DAY_TYPES.BETWEEN && (!isValidMinute(this.startMinute)
                            || !isValidMinute(this.stopMinute)))) {
                valid = false;
            }

            return valid;
        },

        isAfterOrEqualToNext(dateType, startDate, stopDate, timeOfDayType, startTime, stopTime, nextTimer) {
            const nextCellValue = nextTimer?.value || {};
            const nextStartDate = createDate(nextCellValue.startDay, nextCellValue.startMonth, nextCellValue.startYear);
            const nextStartTime = createTime(nextCellValue.startHour, nextCellValue.startMinute, nextCellValue.startAmPm);

            if (dateType === DATE_TYPES.ON && timeOfDayType === TIME_DAY_TYPES.AT) {
                return this.isDateEqual(startDate, nextStartDate) ? !isTimeBefore(startTime, nextStartTime) : this.isDateBefore(nextStartDate, startDate);
            }

            if (dateType === DATE_TYPES.ON && timeOfDayType === DATE_TYPES.BETWEEN) {
                return this.isDateEqual(startDate, nextStartDate) ? !isTimeBefore(stopTime, nextStartTime) : this.isDateBefore(nextStartDate, startDate);
            }

            if (dateType === DATE_TYPES.BETWEEN && timeOfDayType === TIME_DAY_TYPES.AT) {
                return this.isDateEqual(stopDate, nextStartDate) ? !isTimeBefore(startTime, nextStartTime) : this.isDateBefore(nextStartDate, stopDate);
            }

            if (dateType === DATE_TYPES.BETWEEN && timeOfDayType === DATE_TYPES.BETWEEN) {
                return this.isDateEqual(stopDate, nextStartDate) ? !isTimeBefore(stopTime, nextStartTime) : this.isDateBefore(nextStartDate, stopDate);
            }

            if (timeOfDayType === TIME_DAY_TYPES.ANY) {
                return false;
            }

            return true;
        },

        isBeforeOrEqualToPrevious(startDate, startTime, prevTimer) {
            const prevCellValue = prevTimer.value || {};
            const prevDateType = prevCellValue.dateType;
            const prevStartDate = createDate(prevCellValue.startDay, prevCellValue.startMonth, prevCellValue.startYear);
            const prevStopDate = createDate(prevCellValue.stopDay, prevCellValue.stopMonth, prevCellValue.stopYear);
            const prevTimeOfDayType = prevCellValue.timeOfDayType;
            const prevStartTime = createTime(prevCellValue.startHour, prevCellValue.startMinute, prevCellValue.startAmPm);
            const prevStopTime = createTime(prevCellValue.stopHour, prevCellValue.stopMinute, prevCellValue.stopAmPm);

            if (prevDateType === DATE_TYPES.ON && prevTimeOfDayType === TIME_DAY_TYPES.AT) {
                return this.isDateEqual(startDate, prevStartDate) ? !isTimeBefore(prevStartTime, startTime) : this.isDateBefore(startDate, prevStartDate);
            }

            if (prevDateType === DATE_TYPES.ON && prevTimeOfDayType === DATE_TYPES.BETWEEN) {
                return this.isDateEqual(startDate, prevStartDate) ? !isTimeBefore(prevStopTime, startTime) : this.isDateBefore(startDate, prevStartDate);
            }

            if (prevDateType === DATE_TYPES.BETWEEN && prevTimeOfDayType === TIME_DAY_TYPES.AT) {
                return this.isDateEqual(startDate, prevStopDate) ? !isTimeBefore(prevStartTime, startTime) : this.isDateBefore(startDate, prevStopDate);
            }

            if (prevDateType === DATE_TYPES.BETWEEN && prevTimeOfDayType === DATE_TYPES.BETWEEN) {
                return this.isDateEqual(startDate, prevStopDate) ? !isTimeBefore(prevStopTime, startTime) : this.isDateBefore(startDate, prevStopDate);
            }

            if (prevTimeOfDayType === TIME_DAY_TYPES.ANY) {
                return false;
            }

            return true;
        },

        validateDate(date) {
            let validDate = true;

            if ((date.year == null) || !isNumeric(date.year)) {
                validDate = false;
            } else if ((date.month == null) || !isNumeric(date.month) || (date.month > 12)) {
                validDate = false;
            } else if ((date.day == null) || !isNumeric(date.day) || (date.day > 31)) {
                validDate = false;
            }

            return validDate;
        },

        isDateEqual(date1, date2) {
            const firstDate = new Date(`${date1.year}-${date1.month}-${date1.day}`);
            const secondDate = new Date(`${date2.year}-${date2.month}-${date2.day}`);

            return firstDate.toString() === secondDate.toString();
        },

        isDateBefore(date1, date2) {
            const firstDate = new Date(`${date1.year}-${date1.month}-${date1.day}`);
            const secondDate = new Date(`${date2.year}-${date2.month}-${date2.day}`);

            return firstDate < secondDate;
        },
    },
};
</script>

<style lang="scss" scoped>
section.summary {
    padding: 0 5px;
}

.date-timer-formLabel {
    font-size: $font-size-md;
}

.formSpacer {
    padding: 2px;
    display: inline-block;
    max-width: 600px;
}

#timerConfig label {
    vertical-align: baseline;
}

#timerConfig {
    height: 150px;
    width: 300px;
    white-space: nowrap;
    overflow-x: auto;
}

.top-border {
    align-items: center;
    border-top: 1px solid #CCCCCC;
    padding-top: 10px;
}

.top-border-triple-wrapper {
    display: grid;
    grid-template-columns: 15% 25% 20%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: 10px;
}

.top-border-double-wrapper {
    display: grid;
    grid-template-columns: 40% 40%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: 10px;
}

.modal-content-wrapper{
    min-width: 700px;
}

.left-border {
    border-left: 1px solid #CCCCCC;
}

.right-border {
    border-right: 1px solid #CCCCCC;
}

.disabled-option {
    color: #AAAAAA;
}

.timer-option {
    vertical-align: top;
}

#timeOfDay_start .timer-option select,
#timeOfDay_stop .timer-option select{
    width: 55px;
}

.timer-parameter {
    font-weight: 600;
}

.timer-time-zone {
    color: #1E80B9;
    font-weight: 600;
}

#timer-time-zone-padded-box {
    padding-left: 10px;
    padding-right: 10px;
}

#timer-time-zone-details {
    padding-top: 2px;
    white-space: nowrap;
}

#timer-time-zone-example-header {
    padding-top: 15px;
}

.errorMessage {
    color: #dd4949;
    padding-bottom: 5px;
}

.medium-date-picker {
    max-width: 210px;
}

.small-date-picker {
    max-width: 125px;
    margin-right: 30px;
}

.timer-horizontal-radio {
    display: flex;
}

.timer-horizontal-date {
    display: flex;
    padding: 10px 0;
}

.timer-horizontal-date-unique {
    max-width: 375px;
    justify-content: space-between;
    padding: 10px 0;
}

#timerConfig .infusion-calendar-wrapper {
    display: contents;
}

@media only screen and (max-width: 600px) {
    #timerConfig {
        display: block;
        width: 100%
    }
}
@media only screen and (min-width: 601px) {
    #timerConfig {
        min-width: 600px;
    }
}
</style>
